import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "sonner";
import { ActionLink, ActionsCell, ConfirmDeleteDialog, EmptyState, SearchBox, SectionCard, useAccountingPermissions } from "./shared";
import { NEW_LEDGER_OPTION, fixedDepositBlank, fixedDepositStatusLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import FixedDepositForm from "./FixedDepositForm";

function statusPillClass(status) {
  if (status === "ACTIVE") return "bg-emerald-50 text-emerald-700";
  if (status === "MATURED") return "bg-amber-50 text-amber-700";
  return "bg-[#F3F4F6] text-[#6B7280]";
}

export default function FixedDepositsPage() {
  const [deposits, setDeposits] = useState([]);
  const [assetLedgers, setAssetLedgers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState(fixedDepositBlank);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const perms = useAccountingPermissions();
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [depositsRes, ledgersRes, bankRes] = await Promise.all([
        api.get("/society/accounting/engine/fixed-deposits").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/bank-accounts").catch(() => ({ data: [] })),
      ]);
      setDeposits(Array.isArray(depositsRes.data) ? depositsRes.data : []);
      setAssetLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "asset"));
      setBankAccounts(Array.isArray(bankRes.data) ? bankRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load fixed deposits"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedLedgerIds = useMemo(() => new Set(deposits.map((d) => d.linkedLedgerId)), [deposits]);
  const availableLedgers = useMemo(() => assetLedgers.filter((l) => !linkedLedgerIds.has(l.id)), [assetLedgers, linkedLedgerIds]);

  const filtered = useMemo(() => deposits.filter((d) => {
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (q && !String(d.bankName || "").toLowerCase().includes(q) && !String(d.fdNumber || "").toLowerCase().includes(q)) return false;
    return true;
  }), [deposits, search, statusFilter]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/society/accounting/engine/fixed-deposits/${deleteTarget.id}`);
      toast.success("Fixed deposit deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "This fixed deposit cannot be deleted."));
    } finally {
      setDeleting(false);
    }
  };

  const createFd = async () => {
    if (!form.fdNumber.trim()) { toast.error("FD number is required"); return; }
    if (!form.bankAccountId && !form.bankName.trim()) { toast.error("Enter a bank name, or pick a Bank Account"); return; }
    if (!form.principal || Number(form.principal) <= 0) { toast.error("Principal must be greater than zero"); return; }
    if (!form.startDate || !form.maturityDate) { toast.error("Start and maturity dates are required"); return; }
    try {
      setBusy(true);
      let linkedLedgerId = form.linkedLedgerId;
      if (linkedLedgerId === NEW_LEDGER_OPTION) {
        const ledgerName = (form.newLedgerName || `FD ${form.fdNumber}`).trim();
        // Tally-style direct opening balance (Phase 10 redesign): a Fixed
        // Deposit's own Principal IS its opening value when it already
        // existed before go-live - no separate opening-balance field needed
        // on this form, it's just applied straight to the dedicated ledger.
        const ledgerRes = await api.post("/society/accounting/engine/ledgers", { name: ledgerName, ledgerGroup: "asset", openingBalance: Number(form.principal) || 0, openingSide: "debit" });
        linkedLedgerId = ledgerRes.data.id;
      } else if (!linkedLedgerId) {
        toast.error("Choose a linked ledger, or create a new one");
        setBusy(false);
        return;
      }
      await api.post("/society/accounting/engine/fixed-deposits", {
        ...form, linkedLedgerId, principal: Number(form.principal), interestRate: Number(form.interestRate || 0), maturityValue: Number(form.maturityValue || 0),
      });
      toast.success("Fixed deposit created");
      setForm(fixedDepositBlank);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create fixed deposit"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Fixed Deposits</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Society fixed deposit register. A Fixed Deposit is master data describing an instrument - it is not itself an accounting posting.</p>
        </div>
        {!showAddForm && perms.canCreate && (
          <button type="button" onClick={() => setShowAddForm(true)} className="rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white hover:bg-[#374151]">
            + Add fixed deposit
          </button>
        )}
      </div>

      {showAddForm && (
        <SectionCard title="Add fixed deposit">
          <FixedDepositForm form={form} setForm={setForm} bankAccounts={bankAccounts} availableLedgers={availableLedgers} fieldsLocked={false} ledgerLocked={false}
            busy={busy} onSubmit={createFd} onCancel={() => { setShowAddForm(false); setForm(fixedDepositBlank); }} submitLabel="Add fixed deposit" isEdit={false} />
        </SectionCard>
      )}

      <SectionCard>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Search bank or FD number..." />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(fixedDepositStatusLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState>No fixed deposits match this filter. Add one above.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Bank</th>
                  <th className="py-2 pr-3">FD number</th>
                  <th className="py-2 pr-3 text-right">Principal</th>
                  <th className="py-2 pr-3">Maturity date</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Linked ledger</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((fd) => (
                  <tr key={fd.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-3 font-semibold text-[#111827]">{fd.bankName}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{fd.fdNumber}</td>
                    <td className="py-2 pr-3 text-right">₹ {Number(fd.principal || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3">{fd.maturityDate}</td>
                    <td className="py-2 pr-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${statusPillClass(fd.status)}`}>{fixedDepositStatusLabels[fd.status] || fd.status}</span></td>
                    <td className="py-2 pr-3">{fd.linkedLedgerName || "-"}</td>
                    <td className="py-2 pr-3">
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/fixed-deposits/${fd.id}`}>View</ActionLink>
                        {perms.canCreate && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink to={`${ACCOUNTING_BASE}/fixed-deposits/${fd.id}/edit`}>Edit</ActionLink>
                          </>
                        )}
                        {fd.canDelete && perms.canManageMasters && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone="danger" onClick={() => setDeleteTarget(fd)}>Delete</ActionLink>
                          </>
                        )}
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete fixed deposit?"
        identity={deleteTarget ? `${deleteTarget.bankName} - ${deleteTarget.fdNumber}` : ""}
        safeMessage="This fixed deposit is Active with no accounting transactions and can be permanently deleted."
        busy={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
